import type {
  MeshNode,
  MeshSnapshot,
  NexusEvent,
  NodeSpatialProjection,
  OrbitalEntity,
  OrbitalSpatialProjection,
} from "../domain/mesh";
import { formatEvent, getCatalog, type Locale } from "../i18n";

export type PresentedNode = MeshNode &
  NodeSpatialProjection & {
    label: string;
    eyebrow: string;
    description: string;
  };

export type PresentedOrbital = OrbitalEntity &
  OrbitalSpatialProjection & {
    label: string;
    eyebrow: string;
    description: string;
  };

export type PresentedEvent = NexusEvent & {
  title: string;
  detail?: string;
};

export type PresentedSnapshot = Omit<MeshSnapshot, "nodes" | "orbitals" | "events"> & {
  nodes: PresentedNode[];
  orbitalNodes: PresentedOrbital[];
  events: PresentedEvent[];
};

export function presentNode(snapshot: MeshSnapshot, node: MeshNode, locale: Locale): PresentedNode {
  const copy = getCatalog(locale).nodes[node.copyKey];
  const spatial = snapshot.projection.nodes[node.id];
  if (!spatial) throw new Error(`Missing spatial projection for node ${node.id}`);
  return { ...node, ...copy, ...spatial };
}

export function presentOrbital(snapshot: MeshSnapshot, orbital: OrbitalEntity, locale: Locale): PresentedOrbital {
  const copy = getCatalog(locale).orbitals[orbital.copyKey];
  const spatial = snapshot.projection.orbitals[orbital.id];
  if (!spatial) throw new Error(`Missing spatial projection for orbital ${orbital.id}`);
  return { ...orbital, ...copy, ...spatial };
}

export function presentEvent(event: NexusEvent, locale: Locale): PresentedEvent {
  return { ...event, ...formatEvent(event, locale) };
}

export function presentSnapshot(snapshot: MeshSnapshot, locale: Locale): PresentedSnapshot {
  return {
    ...snapshot,
    nodes: snapshot.nodes.map((node) => presentNode(snapshot, node, locale)),
    orbitalNodes: snapshot.orbitals.map((orbital) => presentOrbital(snapshot, orbital, locale)),
    events: snapshot.events.map((event) => presentEvent(event, locale)),
  };
}
